import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { Container } from "@/components/Section";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    phase: "polling", // polling | paid | failed | expired | timeout
    data: null,
    error: null,
  });
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed", data: null, error: "Missing session id." });
      return;
    }
    stoppedRef.current = false;

    const poll = async () => {
      if (stoppedRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(
          `${API}/checkout/status/${sessionId}`,
        );
        if (stoppedRef.current) return;

        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, error: null });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ phase: "timeout", data, error: null });
          return;
        }
        setState((s) => ({ ...s, phase: "polling", data }));
        setTimeout(poll, 2000);
      } catch (err) {
        if (stoppedRef.current) return;
        if (attemptsRef.current >= 8) {
          setState({
            phase: "timeout",
            data: null,
            error: err?.message || "Could not confirm payment",
          });
          return;
        }
        setTimeout(poll, 2000);
      }
    };

    poll();
    return () => {
      stoppedRef.current = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen pt-32 pb-24">
      <SEOHead
        title="Booking Confirmation · Temple of Kulture"
        description="Your booking with Temple of Kulture — confirmation, accessibility info and what to expect on arrival."
      />
      <Container className="max-w-2xl text-center">
        {state.phase === "polling" && (
          <div data-testid="checkout-polling">
            <Loader2 className="h-12 w-12 text-amber-400 animate-spin mx-auto" />
            <p className="mt-8 text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
              Confirming payment
            </p>
            <h1 className="mt-6 font-display font-black uppercase text-4xl sm:text-5xl leading-tight">
              Holding the door open…
            </h1>
            <p className="mt-6 text-zinc-400">
              Just verifying your booking with Stripe. Won't take long.
            </p>
          </div>
        )}

        {state.phase === "paid" && (
          <div data-testid="checkout-paid">
            <CheckCircle2 className="h-14 w-14 text-amber-400 mx-auto" />
            <p className="mt-8 text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
              You're in
            </p>
            <h1 className="mt-6 font-display font-black uppercase text-4xl sm:text-5xl leading-tight">
              Booking confirmed.
            </h1>
            <p className="mt-6 text-zinc-300 text-lg">
              {state.data?.event_title ? (
                <>
                  We've got you for <span className="text-amber-400 font-bold">{state.data.event_title}</span>.
                </>
              ) : (
                <>Your booking is confirmed.</>
              )}
              {" "}A confirmation email is on its way.
            </p>
            {state.data?.amount_total ? (
              <p className="mt-3 text-sm text-zinc-500">
                Paid: £{Number(state.data.amount_total).toFixed(2)}
              </p>
            ) : null}
            <div className="mt-12 flex flex-wrap gap-3 justify-center">
              <Link
                to="/whats-on"
                data-testid="checkout-back-to-calendar"
                className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs"
              >
                See what else is on <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center border border-amber-400/40 hover:border-amber-400 text-amber-400 px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs"
              >
                Return home
              </Link>
            </div>
          </div>
        )}

        {state.phase === "expired" && (
          <Status
            data-testid="checkout-expired"
            icon={<AlertTriangle className="h-12 w-12 text-amber-400 mx-auto" />}
            eyebrow="Session expired"
            title="Let's try that again."
            body="Your Stripe session expired before payment completed. No charge was made — head back and re-book."
          />
        )}

        {state.phase === "timeout" && (
          <Status
            data-testid="checkout-timeout"
            icon={<AlertTriangle className="h-12 w-12 text-amber-400 mx-auto" />}
            eyebrow="Still checking"
            title="Payment status pending."
            body="Stripe is taking longer than usual. Check your email for confirmation, or contact us if it doesn't arrive within an hour."
          />
        )}

        {state.phase === "failed" && (
          <Status
            data-testid="checkout-failed"
            icon={<AlertTriangle className="h-12 w-12 text-red-400 mx-auto" />}
            eyebrow="Something broke"
            title="We couldn't confirm payment."
            body={state.error || "Please try again or contact us."}
          />
        )}
      </Container>
    </div>
  );
}

function Status({ icon, eyebrow, title, body, ...rest }) {
  return (
    <div {...rest}>
      {icon}
      <p className="mt-8 text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
        {eyebrow}
      </p>
      <h1 className="mt-6 font-display font-black uppercase text-4xl sm:text-5xl leading-tight">
        {title}
      </h1>
      <p className="mt-6 text-zinc-300">{body}</p>
      <div className="mt-12 flex flex-wrap gap-3 justify-center">
        <Link
          to="/whats-on"
          className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs"
        >
          Back to calendar <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
